import { getSupabaseClient } from "../supabase/index.js";
import { isBillingStatementInvoiceFrozen } from "./billing-statement-invoice-freeze.js";
/** @typedef {'payment' | 'footer'} InvoiceLayoutTailSectionId */

/** @type {readonly InvoiceLayoutTailSectionId[]} */
export const DEFAULT_INVOICE_LAYOUT_TAIL_SECTION_ORDER = Object.freeze(["payment", "footer"]);

/**
 * @param {unknown} raw
 * @returns {InvoiceLayoutTailSectionId[]}
 */
export function normalizeInvoiceLayoutTailSectionOrder(raw) {
  const allowed = new Set(DEFAULT_INVOICE_LAYOUT_TAIL_SECTION_ORDER);
  const source = Array.isArray(raw) ? raw : [];
  const out = [];
  for (const entry of source) {
    const id = String(entry || "").trim();
    if (!allowed.has(id) || out.includes(id)) continue;
    out.push(id);
  }
  for (const id of DEFAULT_INVOICE_LAYOUT_TAIL_SECTION_ORDER) {
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {unknown} error
 * @returns {string} i18n key
 */
export function resolveInvoiceLayoutSaveErrorKey(error) {
  if (!error) return "trainerstunden.invoiceLayout.saveFailed";
  if (typeof error === "string") {
    if (error === "missing-tenant") return "trainerstunden.invoiceLayout.saveMissingTenant";
    if (error === "no-supabase") return "trainerstunden.invoiceLayout.saveNoSupabase";
    if (error === "table-missing" || error === "schema-missing") {
      return "trainerstunden.invoiceLayout.saveSchemaMissing";
    }
    if (error === "no-changes") return "trainerstunden.invoiceLayout.saveNoChanges";
    if (error === "missing-version") return "trainerstunden.invoiceLayout.activateMissingVersion";
    if (error === "version-not-found") return "trainerstunden.invoiceLayout.activateVersionNotFound";
    if (error === "delete-active-version") return "trainerstunden.invoiceLayout.deleteActiveVersion";
    if (error === "delete-version-in-use") return "trainerstunden.invoiceLayout.deleteVersionInUse";
    if (error === "delete-last-version") return "trainerstunden.invoiceLayout.deleteLastVersion";
    return "trainerstunden.invoiceLayout.saveFailed";
  }
  const code = asStr(error?.code);
  const message = asStr(error?.message);
  if (code === "42P01" || code === "42703") return "trainerstunden.invoiceLayout.saveSchemaMissing";
  if (code === "42501" || /permission denied|policy|row-level security/i.test(message)) {
    return "trainerstunden.invoiceLayout.savePermissionDenied";
  }
  if (code === "23503") return "trainerstunden.invoiceLayout.saveInvalidTenant";
  return "trainerstunden.invoiceLayout.saveFailed";
}

/**
 * @typedef {{
 *   positionNumber: boolean,
 *   sessionDate: boolean,
 *   description: boolean,
 *   playerName: boolean,
 *   trainerName: boolean,
 *   amount: boolean,
 * }} InvoiceLayoutPositionColumns
 */

/**
 * @typedef {{
 *   footerNote?: string,
 *   primaryColor?: string,
 *   accentColor?: string,
 *   showLogo?: boolean,
 *   showServicePeriod?: boolean,
 *   showDueDate?: boolean,
 *   showFooter?: boolean,
 *   showPlayerInPositions?: boolean,
 *   positionColumns?: Partial<InvoiceLayoutPositionColumns>,
 *   tailSectionOrder?: string[],
 * }} InvoiceLayoutConfig
 */

/**
 * @typedef {{
 *   id: string,
 *   tenant_id: string,
 *   version_number: number,
 *   label?: string,
 *   valid_from: string,
 *   logo_url?: string,
 *   config: InvoiceLayoutConfig,
 *   created_at?: string,
 * }} InvoiceLayoutVersion
 */

/**
 * @returns {InvoiceLayoutConfig}
 */
export function defaultInvoiceLayoutPositionColumns() {
  return {
    positionNumber: true,
    sessionDate: true,
    description: true,
    playerName: true,
    trainerName: false,
    amount: true,
  };
}

/**
 * @param {unknown} raw
 * @param {boolean} [legacyShowPlayerInPositions]
 * @returns {InvoiceLayoutPositionColumns}
 */
export function normalizeInvoiceLayoutPositionColumns(raw, legacyShowPlayerInPositions = true) {
  const base = defaultInvoiceLayoutPositionColumns();
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const playerDefault =
    typeof legacyShowPlayerInPositions === "boolean"
      ? legacyShowPlayerInPositions
      : source.playerName !== false;
  return {
    positionNumber: true,
    sessionDate: source.sessionDate !== false,
    description: true,
    playerName: source.playerName === false ? false : playerDefault,
    trainerName: source.trainerName === true,
    amount: true,
  };
}

export function defaultInvoiceLayoutConfig() {
  return {
    footerNote: "",
    primaryColor: "#111827",
    accentColor: "#059669",
    showLogo: true,
    showServicePeriod: true,
    showDueDate: true,
    showFooter: true,
    showPlayerInPositions: true,
    positionColumns: defaultInvoiceLayoutPositionColumns(),
    tailSectionOrder: [...DEFAULT_INVOICE_LAYOUT_TAIL_SECTION_ORDER],
  };
}

/**
 * @param {unknown} raw
 * @returns {InvoiceLayoutConfig}
 */
/**
 * @param {unknown} hex
 * @param {string} [fallback]
 * @returns {{ hex: string, rgb: [number, number, number] }}
 */
export function parseInvoiceLayoutHexColor(hex, fallback = "#111827") {
  const normalize = (value) => {
    const raw = asStr(value).replace(/^#/, "");
    return /^[0-9a-f]{6}$/i.test(raw) ? raw.toLowerCase() : "";
  };
  const picked = normalize(hex) || normalize(fallback) || "111827";
  return {
    hex: `#${picked}`,
    rgb: [
      parseInt(picked.slice(0, 2), 16) / 255,
      parseInt(picked.slice(2, 4), 16) / 255,
      parseInt(picked.slice(4, 6), 16) / 255,
    ],
  };
}

/**
 * @param {{ logo_url?: string, config?: unknown }|InvoiceLayoutVersion|null|undefined} source
 */
export function invoiceLayoutContentEquals(left, right) {
  const logoA = asStr(left?.logo_url);
  const logoB = asStr(right?.logo_url);
  if (logoA !== logoB) return false;
  const cfgA = normalizeInvoiceLayoutConfig(left?.config);
  const cfgB = normalizeInvoiceLayoutConfig(right?.config);
  return JSON.stringify(cfgA) === JSON.stringify(cfgB);
}

/**
 * @param {{ logo_url?: string, config?: unknown }|InvoiceLayoutVersion|null|undefined} previous
 * @param {{ logo_url?: string, config?: unknown }|InvoiceLayoutVersion|null|undefined} next
 * @returns {string[]}
 */
export function collectInvoiceLayoutContentChangeKeys(previous, next) {
  /** @type {string[]} */
  const keys = [];
  const prevLogo = asStr(previous?.logo_url);
  const nextLogo = asStr(next?.logo_url);
  if (prevLogo !== nextLogo) keys.push("logo");

  const prevCfg = normalizeInvoiceLayoutConfig(previous?.config);
  const nextCfg = normalizeInvoiceLayoutConfig(next?.config);

  if (prevCfg.primaryColor !== nextCfg.primaryColor) keys.push("primaryColor");
  if (prevCfg.accentColor !== nextCfg.accentColor) keys.push("accentColor");
  if (prevCfg.footerNote !== nextCfg.footerNote) keys.push("footerNote");

  const pushToggle = (field, onKey, offKey) => {
    if (prevCfg[field] === nextCfg[field]) return;
    keys.push(nextCfg[field] ? onKey : offKey);
  };

  pushToggle("showLogo", "showLogoOn", "showLogoOff");
  pushToggle("showServicePeriod", "showServicePeriodOn", "showServicePeriodOff");
  pushToggle("showDueDate", "showDueDateOn", "showDueDateOff");
  pushToggle("showFooter", "showFooterOn", "showFooterOff");

  const pushColumnToggle = (column, onKey, offKey) => {
    const prevVal = prevCfg.positionColumns?.[column];
    const nextVal = nextCfg.positionColumns?.[column];
    if (prevVal === nextVal) return;
    keys.push(nextVal ? onKey : offKey);
  };

  pushColumnToggle("sessionDate", "positionSessionDateOn", "positionSessionDateOff");
  pushColumnToggle("playerName", "positionPlayerOn", "positionPlayerOff");
  pushColumnToggle("trainerName", "positionTrainerOn", "positionTrainerOff");

  if (JSON.stringify(prevCfg.tailSectionOrder) !== JSON.stringify(nextCfg.tailSectionOrder)) {
    keys.push("tailSectionOrder");
  }

  return keys;
}

/**
 * @param {string[]} changeKeys
 * @param {(key: string, params?: Record<string, unknown>) => string} translate
 * @param {{ maxParts?: number }} [options]
 */
export function formatInvoiceLayoutChangeSummary(changeKeys, translate, options = {}) {
  const keys = Array.isArray(changeKeys) ? changeKeys.filter(Boolean) : [];
  const maxParts = Math.max(1, Number(options.maxParts) || 3);
  if (!keys.length) {
    return translate("trainerstunden.invoiceLayout.change.generic");
  }

  const parts = keys.slice(0, maxParts).map((key) => {
    const i18nKey = `trainerstunden.invoiceLayout.change.${key}`;
    const label = translate(i18nKey);
    return label !== i18nKey ? label : key;
  });

  if (keys.length > maxParts) {
    parts.push(
      translate("trainerstunden.invoiceLayout.change.more", {
        count: keys.length - maxParts,
      })
    );
  }

  return parts.join(", ");
}

/**
 * @deprecated Nur noch für Abwärtskompatibilität — Reaktivierung nutzt activateInvoiceLayoutVersion.
 * @param {InvoiceLayoutVersion|null|undefined} sourceVersion
 * @param {{ label?: string, valid_from?: string }} [options]
 */
export function buildInvoiceLayoutVersionRestoreInput(sourceVersion, options = {}) {
  const source = normalizeInvoiceLayoutVersionRow(sourceVersion);
  if (!source) return null;
  return {
    label: asStr(options.label) || `Version ${source.version_number}`,
    valid_from: asStr(options.valid_from) || new Date().toISOString(),
    logo_url: asStr(source.logo_url),
    config: normalizeInvoiceLayoutConfig(source.config),
  };
}

export function normalizeInvoiceLayoutConfig(raw) {
  const base = defaultInvoiceLayoutConfig();
  if (!raw || typeof raw !== "object") return base;
  const positionColumns = normalizeInvoiceLayoutPositionColumns(
    raw.positionColumns,
    raw.showPlayerInPositions !== false
  );
  return {
    footerNote: asStr(raw.footerNote) || "",
    primaryColor: parseInvoiceLayoutHexColor(raw.primaryColor, base.primaryColor).hex,
    accentColor: parseInvoiceLayoutHexColor(raw.accentColor, base.accentColor).hex,
    showLogo: raw.showLogo !== false,
    showServicePeriod: raw.showServicePeriod !== false,
    showDueDate: raw.showDueDate !== false,
    showFooter: raw.showFooter !== false,
    showPlayerInPositions: positionColumns.playerName,
    positionColumns,
    tailSectionOrder: normalizeInvoiceLayoutTailSectionOrder(raw.tailSectionOrder),
  };
}

/**
 * @param {unknown} row
 * @returns {InvoiceLayoutVersion|null}
 */
export function normalizeInvoiceLayoutVersionRow(row) {
  if (!row || typeof row !== "object") return null;
  const id = asStr(row.id);
  const tenantId = asStr(row.tenant_id);
  if (!id || !tenantId) return null;
  return {
    id,
    tenant_id: tenantId,
    version_number: Number(row.version_number) || 1,
    label: asStr(row.label) || undefined,
    valid_from: asStr(row.valid_from) || new Date().toISOString(),
    logo_url: asStr(row.logo_url) || undefined,
    config: normalizeInvoiceLayoutConfig(row.config),
    created_at: asStr(row.created_at) || undefined,
  };
}

/**
 * @param {InvoiceLayoutVersion|null|undefined} version
 */
export function buildInvoiceLayoutSnapshot(version) {
  if (!version) {
    return {
      version_id: "",
      version_number: 0,
      valid_from: "",
      logo_url: "",
      config: defaultInvoiceLayoutConfig(),
    };
  }
  return {
    version_id: version.id,
    version_number: version.version_number,
    valid_from: version.valid_from,
    logo_url: asStr(version.logo_url),
    label: asStr(version.label),
    config: normalizeInvoiceLayoutConfig(version.config),
  };
}

/**
 * @param {unknown} raw
 */
export function normalizeInvoiceLayoutSnapshot(raw) {
  if (!raw || typeof raw !== "object") {
    return buildInvoiceLayoutSnapshot(null);
  }
  return {
    version_id: asStr(raw.version_id),
    version_number: Number(raw.version_number) || 0,
    valid_from: asStr(raw.valid_from),
    logo_url: asStr(raw.logo_url),
    label: asStr(raw.label),
    config: normalizeInvoiceLayoutConfig(raw.config),
  };
}

/**
 * @param {InvoiceLayoutVersion[]} versions
 * @param {string|Date} [at]
 */
export function resolveInvoiceLayoutVersionForDate(versions, at = new Date()) {
  const list = (Array.isArray(versions) ? versions : [])
    .map(normalizeInvoiceLayoutVersionRow)
    .filter(Boolean);
  if (!list.length) return null;
  const atMs = new Date(at).getTime();
  const eligible = list
    .filter((row) => {
      const ms = new Date(row.valid_from).getTime();
      return Number.isFinite(ms) && ms <= atMs;
    })
    .sort((a, b) => new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime());
  if (eligible.length) return eligible[0];
  return [...list].sort((a, b) => a.version_number - b.version_number)[0] || null;
}

/**
 * @param {object|null|undefined} statement
 * @param {InvoiceLayoutVersion[]} versions
 */
export function resolveInvoiceLayoutForStatement(statement, versions) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (isBillingStatementInvoiceFrozen(st) && st.invoice_layout_snapshot) {
    return normalizeInvoiceLayoutSnapshot(st.invoice_layout_snapshot);
  }
  const snap = normalizeInvoiceLayoutSnapshot(st.invoice_layout_snapshot);
  if (snap.version_id && isBillingStatementInvoiceFrozen(st)) return snap;
  const version = resolveInvoiceLayoutVersionForDate(versions, new Date());
  return buildInvoiceLayoutSnapshot(version);
}

/**
 * @param {unknown} statements
 * @returns {Set<string>}
 */
export function collectInvoiceLayoutVersionIdsInUse(statements) {
  const ids = new Set();
  const list = Array.isArray(statements) ? statements : [];
  for (const st of list) {
    if (!st || typeof st !== "object") continue;
    if (!isBillingStatementInvoiceFrozen(st)) continue;
    const directId = asStr(st.invoice_layout_version_id);
    if (directId) ids.add(directId);
    const snap = st.invoice_layout_snapshot;
    if (snap && typeof snap === "object") {
      const snapId = asStr(snap.version_id);
      if (snapId) ids.add(snapId);
    }
  }
  return ids;
}

/**
 * @param {InvoiceLayoutVersion|null|undefined} version
 * @param {{
 *   activeVersionId?: string,
 *   versionCount?: number,
 *   usedVersionIds?: Set<string>|string[],
 * }} [context]
 */
export function evaluateInvoiceLayoutVersionDelete(version, context = {}) {
  const id = asStr(version?.id);
  if (!id) return { ok: false, error: "missing-version" };

  const activeId = asStr(context.activeVersionId);
  if (activeId && id === activeId) return { ok: false, error: "delete-active-version" };

  const versionCount = Number(context.versionCount) || 0;
  if (versionCount <= 1) return { ok: false, error: "delete-last-version" };

  const used = context.usedVersionIds;
  if (used instanceof Set && used.has(id)) return { ok: false, error: "delete-version-in-use" };
  if (Array.isArray(used) && used.includes(id)) return { ok: false, error: "delete-version-in-use" };

  return { ok: true };
}

/**
 * @param {string} tenantId
 */
export async function loadInvoiceLayoutVersionIdsInUse(tenantId) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: true, data: new Set() };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainerstunden_billing_statements")
      .select("invoice_layout_version_id, invoice_layout_snapshot, status")
      .eq("tenant_id", tid)
      .limit(5000);

    if (error) {
      const code = asStr(error?.code);
      if (code === "42P01" || code === "42703") return { ok: true, data: new Set() };
      return { ok: false, error };
    }

    return { ok: true, data: collectInvoiceLayoutVersionIdsInUse(data) };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 */
export async function loadInvoiceLayoutVersions(tenantId) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: true, data: [], reason: "missing-tenant" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainerstunden_invoice_layout_versions")
      .select("*")
      .eq("tenant_id", tid)
      .order("version_number", { ascending: true });

    if (error) {
      const code = asStr(error?.code);
      if (code === "42P01") return { ok: true, data: [], reason: "table-missing" };
      return { ok: false, error };
    }

    const rows = (Array.isArray(data) ? data : [])
      .map(normalizeInvoiceLayoutVersionRow)
      .filter(Boolean);
    return { ok: true, data: rows };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 */
export async function ensureDefaultInvoiceLayoutVersion(tenantId) {
  const loaded = await loadInvoiceLayoutVersions(tenantId);
  if (!loaded.ok) return loaded;
  if ((loaded.data || []).length) return loaded;
  return createInvoiceLayoutVersion(tenantId, {
    label: "Standard",
    valid_from: new Date().toISOString(),
    logo_url: "",
    config: defaultInvoiceLayoutConfig(),
  });
}

/**
 * @param {string} tenantId
 * @param {{ label?: string, valid_from?: string, logo_url?: string, config?: InvoiceLayoutConfig }} input
 */
export async function createInvoiceLayoutVersion(tenantId, input = {}) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: false, error: "missing-tenant" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const existing = await loadInvoiceLayoutVersions(tid);
    const nextNumber =
      (existing.ok && existing.data?.length
        ? Math.max(...existing.data.map((row) => row.version_number))
        : 0) + 1;

    const row = {
      tenant_id: tid,
      version_number: nextNumber,
      label: asStr(input.label) || `Version ${nextNumber}`,
      valid_from: asStr(input.valid_from) || new Date().toISOString(),
      logo_url: asStr(input.logo_url) || null,
      config: normalizeInvoiceLayoutConfig(input.config),
    };

    const { data, error } = await supabase
      .from("trainerstunden_invoice_layout_versions")
      .insert(row)
      .select("*")
      .single();

    if (error) {
      const code = asStr(error?.code);
      if (code === "42P01") return { ok: false, error: "table-missing" };
      return { ok: false, error };
    }

    const normalized = normalizeInvoiceLayoutVersionRow(data);
    return { ok: true, data: normalized };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 * @param {string} versionId
 * @param {{ valid_from?: string, label?: string }} [options]
 */
export async function activateInvoiceLayoutVersion(tenantId, versionId, options = {}) {
  const tid = asStr(tenantId);
  const vid = asStr(versionId);
  if (!tid) return { ok: false, error: "missing-tenant" };
  if (!vid) return { ok: false, error: "missing-version" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const validFrom = asStr(options.valid_from) || new Date().toISOString();
    /** @type {Record<string, unknown>} */
    const patch = { valid_from: validFrom };
    if (asStr(options.label)) patch.label = asStr(options.label);

    const { data, error } = await supabase
      .from("trainerstunden_invoice_layout_versions")
      .update(patch)
      .eq("tenant_id", tid)
      .eq("id", vid)
      .select("*")
      .single();

    if (error) {
      const code = asStr(error?.code);
      if (code === "42P01") return { ok: false, error: "table-missing" };
      if (code === "PGRST116") return { ok: false, error: "version-not-found" };
      return { ok: false, error };
    }

    const normalized = normalizeInvoiceLayoutVersionRow(data);
    if (!normalized) return { ok: false, error: "version-not-found" };
    return { ok: true, data: normalized };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 * @param {string} versionId
 * @param {{ usedVersionIds?: Set<string> }} [options]
 */
export async function deleteInvoiceLayoutVersion(tenantId, versionId, options = {}) {
  const tid = asStr(tenantId);
  const vid = asStr(versionId);
  if (!tid) return { ok: false, error: "missing-tenant" };
  if (!vid) return { ok: false, error: "missing-version" };

  try {
    const loaded = await loadInvoiceLayoutVersions(tid);
    if (!loaded.ok) return loaded;

    const versions = loaded.data || [];
    const version = versions.find((row) => row.id === vid);
    if (!version) return { ok: false, error: "version-not-found" };

    const active = resolveInvoiceLayoutVersionForDate(versions, new Date());
    let usedVersionIds = options.usedVersionIds;
    if (!(usedVersionIds instanceof Set)) {
      const usedRes = await loadInvoiceLayoutVersionIdsInUse(tid);
      if (!usedRes.ok) return usedRes;
      usedVersionIds = usedRes.data || new Set();
    }

    const check = evaluateInvoiceLayoutVersionDelete(version, {
      activeVersionId: active?.id,
      versionCount: versions.length,
      usedVersionIds,
    });
    if (!check.ok) return check;

    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { error } = await supabase
      .from("trainerstunden_invoice_layout_versions")
      .delete()
      .eq("tenant_id", tid)
      .eq("id", vid);

    if (error) {
      const code = asStr(error?.code);
      if (code === "42P01") return { ok: false, error: "table-missing" };
      return { ok: false, error };
    }

    return { ok: true, data: { id: vid } };
  } catch (e) {
    return { ok: false, error: e };
  }
}
